// src/components/map/hooks/adapters/useNetworkVisualizationAdapter.ts
//
// Adapter hook extracted from NetworkVisualizationOverlay.tsx.
// Encapsulates all imperative Mapbox interactions (source/layer lifecycle,
// network line rendering, facility highlight overlay, icon-size updates),
// selected-well change detection with pan-to-bounds coordination,
// resize observation, and React Query data-fetching orchestration.
//
// The consuming component is responsible only for rendering the two
// DraggableLegendBase panels (network options + depth legend),
// the NetworkMapOptions sub-component, and styled layout.
import useResizeObserver from "@react-hook/resize-observer";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import colormap from "colormap";
import type { AnyLayer, GeoJSONSource } from "mapbox-gl";
import { useMapStore } from "stores/map";
import {
  selectDisplaySettings,
  selectIsWellNetworkFilterActivated,
  selectNetworkDepthLegend,
  selectSelectedWells,
  selectShowNetworkOptions,
  selectWellNetworkMapRequest
} from "stores/map/mapSelectors";
import type { INetworkDepthLegend, WellNetworkRequest } from "stores/map/mapTypes";

import { useUserEvents } from "hooks";

import { FACILITY_LAYER, FACILITY_TYPE_ENUM } from "../../../../constants";
import { isStartEndDateValid } from "../../utils/dateUtils";
import { clearGeojsonSource } from "../../utils/geojsonSource";
import { safeRemoveLayerAndSource } from "../../utils/layerOperations";
import {
  HIGHLIGHTED_FACILITY,
  HIGHLIGHTED_NETWORK_FACILITIES,
  NETWORK_DEPTH_LEGEND_TITLE,
  WELL_NETWORK_MAP
} from "../../utils/networkLayerConstants";
import { useClearWellToFacilityNetwork } from "../useClearWellToFacilityNetwork";
import { WellNetworkResult, useGetWellNetworkMap } from "../useGetWellNetworkMap";
import { useMapContext } from "../useMapContext";
import { useNetworkPanToBounds } from "../useNetworkPanToBounds";

// ── Types ────────────────────────────────────────────────────────────────────

export interface NetworkVisualizationAdapterState {
  /** Whether the network visualization is active and should render UI. */
  readonly isActive: boolean;

  /** Network line width range (local state for the options panel). */
  readonly networkLineWidth: readonly [number, number];

  /** Well network map data (from React Query). */
  readonly wellNetworkMap: Maybe<WellNetworkResult>;

  /** Network depth legend from Zustand store. */
  readonly networkDepthLegend: INetworkDepthLegend;

  /** Well network map request from Zustand store. */
  readonly wellNetworkMapRequest: WellNetworkRequest;

  /** Container ref for DraggableLegendBase parent dimensions. */
  readonly containerRef: React.RefObject<HTMLDivElement>;

  /** Container width (from resize observer). */
  readonly containerWidth: number;

  /** Container height (from resize observer). */
  readonly containerHeight: number;
}

export interface NetworkVisualizationAdapterActions {
  /** Clear the entire network visualization (layers + store state). */
  readonly clearNetworkMap: () => void;

  /** Update the well network map request (from options panel changes). */
  readonly updateWellNetworkMapRequest: (partial: Partial<WellNetworkRequest>) => void;

  /** Update the network line width. */
  readonly setNetworkLineWidth: (width: [number, number]) => void;
}

export type UseNetworkVisualizationAdapter = NetworkVisualizationAdapterState &
  NetworkVisualizationAdapterActions;

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Adapter hook that manages the network visualization on the Mapbox map.
 *
 * Responsibilities:
 * - Reads well network map request, selected wells, display settings, and
 *   network depth legend from Zustand.
 * - Orchestrates `useGetWellNetworkMap` React Query hook for data fetching.
 * - Creates and tears down GeoJSON sources and layers for network lines
 *   and highlighted facility icons.
 * - Updates facility icon sizes when display settings change.
 * - Detects selected-well changes and coordinates pan-to-bounds.
 * - Manages container resize observation for DraggableLegendBase positioning.
 * - Fires analytics events for network showing / toggling.
 * - Cleans up all Mapbox resources when the network is cleared.
 */
export function useNetworkVisualizationAdapter(): UseNetworkVisualizationAdapter {
  // ── Map context ────────────────────────────────────────────────────

  const { mapbox, isMapInitialized } = useMapContext();

  // ── Composed hooks ─────────────────────────────────────────────────

  const { clearWellToFacilityNetwork } = useClearWellToFacilityNetwork({
    mapbox
  });
  const { panToNetworkBounds } = useNetworkPanToBounds(mapbox);
  const { sendWellNetworkShowingEvent, sendMidstreamNavigatorButtonToggledEvent } =
    useUserEvents();

  // ── Zustand store: reads ───────────────────────────────────────────

  const wellNetworkMapRequest = useMapStore(selectWellNetworkMapRequest);
  const showNetworkVisualOptions = useMapStore(selectShowNetworkOptions);
  const networkDepthLegend = useMapStore(selectNetworkDepthLegend);
  const displaySettings = useMapStore(selectDisplaySettings);
  const selectedWells = useMapStore(selectSelectedWells);
  const isWellNetworkActive = useMapStore(selectIsWellNetworkFilterActivated);

  // ── Zustand store: actions ─────────────────────────────────────────

  const setShowNetworkOptions = useMapStore((s) => s.setShowNetworkOptions);
  const clearNetworkDepthLegend = useMapStore((s) => s.clearNetworkDepthLegend);
  const setNetworkDepthLegend = useMapStore((s) => s.setNetworkDepthLegend);
  const setWellNetworkFilterActivated = useMapStore(
    (s) => s.setWellNetworkFilterActivated
  );
  const setWellNetworkMapRequest = useMapStore((s) => s.setWellNetworkMapRequest);

  // ── React Query ────────────────────────────────────────────────────

  const { data: wellNetworkMap } = useGetWellNetworkMap(wellNetworkMapRequest);

  // ── Local state ────────────────────────────────────────────────────

  const [networkLineWidth, setNetworkLineWidthState] = useState<[number, number]>([
    3, 12
  ]);
  const [panCompleted, setPanCompleted] = useState(0);

  // ── Refs ───────────────────────────────────────────────────────────

  const facilityIconSizeRef = useRef(1.0);
  const prevSelectedWellsRef = useRef<string[]>([]);
  const lastPannedOnlyPrimaryPathRef = useRef<boolean | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Container dimensions (resize observer) ─────────────────────────

  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  useResizeObserver(containerRef, (entry) => {
    setContainerHeight(entry.contentRect.height);
    setContainerWidth(entry.contentRect.width);
  });

  // ── Public actions ─────────────────────────────────────────────────

  const clearNetworkMap = useCallback(() => {
    clearWellToFacilityNetwork();
  }, [clearWellToFacilityNetwork]);

  const updateWellNetworkMapRequest = useCallback(
    (partial: Partial<WellNetworkRequest>) => {
      setWellNetworkMapRequest({
        ...wellNetworkMapRequest,
        ...partial
      });
    },
    [wellNetworkMapRequest, setWellNetworkMapRequest]
  );

  const setNetworkLineWidth = useCallback((width: [number, number]) => {
    setNetworkLineWidthState(width);
  }, []);

  // ── Effect: sync facility icon size ref ────────────────────────────

  useEffect(() => {
    facilityIconSizeRef.current = displaySettings?.facilityIconSize || 1.0;
  }, [displaySettings?.facilityIconSize]);

  // ── Effect: update network facility layer icon size ────────────────

  useEffect(() => {
    if (!mapbox || !displaySettings?.facilityIconSize) return;

    if (mapbox.getLayer(HIGHLIGHTED_NETWORK_FACILITIES)) {
      const newIconSizeExpression = [
        "interpolate",
        ["linear"],
        ["zoom"],
        6,
        0.2 * displaySettings.facilityIconSize,
        10,
        1.0 * displaySettings.facilityIconSize,
        12,
        1.5 * displaySettings.facilityIconSize,
        22,
        2.0 * displaySettings.facilityIconSize
      ];

      mapbox.setLayoutProperty(
        HIGHLIGHTED_NETWORK_FACILITIES,
        "icon-size",
        newIconSizeExpression
      );
    }
  }, [mapbox, displaySettings?.facilityIconSize]);

  // ── Effect: network map visualization (source/layer lifecycle) ─────
  //
  // This is the large effect (~200 lines) that:
  // 1. Validates the network map data and request
  // 2. Creates / updates the WELL_NETWORK_MAP line layer with color+width styles
  // 3. Sets the network depth legend in the store
  // 4. Extracts facility coordinates from the GeoJSON features
  // 5. Queries facility properties from the FACILITY_LAYER source
  // 6. Creates the HIGHLIGHTED_NETWORK_FACILITIES symbol layer

  useEffect(() => {
    const hasWellIdsInRequest =
      wellNetworkMapRequest?.wellIds && wellNetworkMapRequest.wellIds.length > 0;

    if (!mapbox || !wellNetworkMap || !hasWellIdsInRequest) {
      return;
    }

    const keys = Object.keys(wellNetworkMap.wellFeatureCollections);

    const isDateValid = isStartEndDateValid(
      wellNetworkMap.startDate,
      wellNetworkMap.endDate
    );

    const currentSelectedWellIds = Object.keys(selectedWells || {});

    // Show network visualization options early so it stays open even
    // when no data for a specific month.
    setShowNetworkOptions(true);

    // Use the currently selected well if available, otherwise fallback to first available.
    const selectedWellId =
      currentSelectedWellIds.length > 0 && keys.includes(currentSelectedWellIds[0])
        ? currentSelectedWellIds[0]
        : keys[0];

    if (keys.length === 0 && !isDateValid) {
      clearGeojsonSource(mapbox, WELL_NETWORK_MAP);
      clearGeojsonSource(mapbox, HIGHLIGHTED_FACILITY);
      clearNetworkDepthLegend();
      safeRemoveLayerAndSource(mapbox, HIGHLIGHTED_NETWORK_FACILITIES);
      setShowNetworkOptions(false);
      toast.warn("No facility network found for selected well.");
      return;
    } else if (keys.length === 0 && isDateValid) {
      toast.info("No facility network found for selected well this month.");
      clearGeojsonSource(mapbox, WELL_NETWORK_MAP);
      clearGeojsonSource(mapbox, HIGHLIGHTED_FACILITY);
      clearNetworkDepthLegend();
      safeRemoveLayerAndSource(mapbox, HIGHLIGHTED_NETWORK_FACILITIES);
      return;
    }

    // ── Line color + width styles ──────────────────────────────────

    const maxDepth = wellNetworkMap.maxDepth;

    const colors: string[] = colormap({
      colormap: "autumn",
      nshades: Math.max(3, maxDepth + 1),
      format: "hex",
      alpha: 1
    });

    const geojson = wellNetworkMap.wellFeatureCollections[selectedWellId];

    const depthAndColors = colors
      .map((color: string, index: number) => [index, color])
      .flat();

    const lineColorStyle = ["match", ["get", "depth"], ...depthAndColors, "#000000"];

    const lineWidthStyle = [
      "interpolate",
      ["linear"],
      ["get", "fraction"],
      0.01,
      networkLineWidth[0],
      1.0,
      networkLineWidth[1]
    ];

    // ── Update depth legend in store ───────────────────────────────

    setNetworkDepthLegend({
      title: NETWORK_DEPTH_LEGEND_TITLE,
      legendItems: colors.map((color: string, index: number) => ({
        color,
        title: index.toString()
      })),
      visible: true
    });

    // ── Set GeoJSON source and add line layer ──────────────────────

    const networkMapSource = mapbox.getSource(WELL_NETWORK_MAP) as GeoJSONSource;

    if (networkMapSource) {
      networkMapSource.setData(geojson);
    } else {
      mapbox.addSource(WELL_NETWORK_MAP, {
        type: "geojson",
        data: geojson
      });

      mapbox.addLayer({
        id: WELL_NETWORK_MAP,
        type: "line",
        source: WELL_NETWORK_MAP,
        paint: {
          "line-color": lineColorStyle,
          "line-width": lineWidthStyle
        }
      } as AnyLayer);
    }

    if (mapbox.getLayer(WELL_NETWORK_MAP)) {
      mapbox.setPaintProperty(WELL_NETWORK_MAP, "line-width", lineWidthStyle);
      mapbox.setPaintProperty(WELL_NETWORK_MAP, "line-color", lineColorStyle);
    }

    // ── Extract facility coordinates + depths from GeoJSON ─────────

    const facilityToDepthMap = new Map<string, number>();
    const facilityToCoordinatesMap = new Map<string, [number, number]>();

    geojson.features.forEach((feature, index) => {
      if (feature.properties && feature.geometry?.type === "LineString") {
        const depth = feature.properties.depth ?? 0;
        const coordinates = (feature.geometry as GeoJSON.LineString).coordinates;

        // Extract 'to' facility (endpoint of the line) — always add/update
        if (feature.properties.to && coordinates.length >= 2) {
          const toCoords = coordinates[coordinates.length - 1] as [number, number];
          facilityToDepthMap.set(feature.properties.to, depth);
          facilityToCoordinatesMap.set(feature.properties.to, toCoords);
        }

        // Extract 'from' facility (startpoint of the line)
        if (feature.properties.from && coordinates.length >= 2) {
          const fromCoords = coordinates[0] as [number, number];

          if (index === 0) {
            // First feature: source well, set depth to 0
            facilityToDepthMap.set(feature.properties.from, 0);
            facilityToCoordinatesMap.set(feature.properties.from, fromCoords);
          } else if (!facilityToDepthMap.has(feature.properties.from)) {
            // Subsequent features: only add if not already present
            facilityToDepthMap.set(feature.properties.from, depth);
            facilityToCoordinatesMap.set(feature.properties.from, fromCoords);
          }
        }
      }
    });

    // ── Build highlighted facilities symbol layer ──────────────────

    if (facilityToDepthMap.size > 0) {
      const facilityIdArray = Array.from(facilityToDepthMap.keys());

      // Query all facilities from the source (not restricted to viewport)
      const foundFacilities = mapbox.querySourceFeatures(FACILITY_LAYER, {
        sourceLayer: "eva-facility",
        filter: [
          "has",
          ["get", "Uwi"],
          [
            "literal",
            facilityIdArray.reduce((obj, id) => {
              obj[id] = true;
              return obj;
            }, {} as Record<string, boolean>)
          ]
        ]
      });

      const facilityPropertiesMap = new Map<string, Record<string, unknown>>();
      foundFacilities.forEach((feat) => {
        if (feat.properties?.Uwi) {
          facilityPropertiesMap.set(feat.properties.Uwi, feat.properties);
        }
      });

      // Create highlighted features for ALL facilities in the network
      const highlightedFeatures = Array.from(facilityToDepthMap.keys())
        .map((facilityId) => {
          const depth = facilityToDepthMap.get(facilityId) ?? 0;
          const coords = facilityToCoordinatesMap.get(facilityId) || [0, 0];
          const facilityProps = facilityPropertiesMap.get(facilityId);

          return {
            type: "Feature" as const,
            geometry: {
              type: "Point" as const,
              coordinates: coords
            },
            properties: {
              Uwi: facilityId,
              facilityType: facilityProps?.facilityType,
              ...(facilityProps || {}),
              highlighted: true,
              networkDepth: depth
            }
          };
        })
        .filter((feature) => feature.properties.facilityType !== undefined);

      const highlightedFacilitiesCollection = {
        type: "FeatureCollection" as const,
        features: highlightedFeatures
      };

      // Remove existing layer and source before re-adding
      safeRemoveLayerAndSource(mapbox, HIGHLIGHTED_NETWORK_FACILITIES);

      // Reuse the same depth-based color mapping
      const facilityDepthAndColors = colors
        .map((color: string, index: number) => [index, color])
        .flat();

      mapbox.addSource(HIGHLIGHTED_NETWORK_FACILITIES, {
        type: "geojson",
        data: highlightedFacilitiesCollection
      });

      mapbox.addLayer({
        id: HIGHLIGHTED_NETWORK_FACILITIES,
        type: "symbol",
        source: HIGHLIGHTED_NETWORK_FACILITIES,
        paint: {
          "icon-color": [
            "match",
            ["get", "networkDepth"],
            ...facilityDepthAndColors,
            colors[0] || "#FF0000"
          ]
        },
        layout: {
          "icon-image": [
            "match",
            ["get", "facilityType"],
            "CompressorStation",
            "compressor-station",
            FACILITY_TYPE_ENUM.GasPlant,
            "gas-plant",
            "Battery",
            "battery",
            "MeteringStation",
            "metering-station",
            "WastePlant",
            "waste-plant",
            "InjectionDisposalFacility",
            "injection-facility",
            "GatheringSystem",
            "gathering-system",
            /* default */ "default-facility"
          ],
          "icon-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            6,
            0.2 * facilityIconSizeRef.current,
            10,
            1.0 * facilityIconSizeRef.current,
            12,
            1.5 * facilityIconSizeRef.current,
            22,
            2.0 * facilityIconSizeRef.current
          ],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true
        }
      });
    }
  }, [
    wellNetworkMap,
    networkLineWidth,
    mapbox,
    isMapInitialized,
    setShowNetworkOptions,
    clearNetworkDepthLegend,
    setNetworkDepthLegend,
    wellNetworkMapRequest?.wellIds,
    panCompleted,
    selectedWells
  ]);

  // ── Effect: selected-well change detection + pan-to-bounds ─────────
  //
  // Detects when the selected well changes or the network filter is activated,
  // triggers pan-to-bounds, waits for map idle + tile loading, fires analytics
  // events, then increments `panCompleted` to trigger the visualization effect
  // to re-query facilities in the new viewport.

  useEffect(() => {
    if (!wellNetworkMap || !showNetworkVisualOptions) {
      return;
    }

    const currentWellIds = Object.keys(selectedWells || {});
    const prevWellIds = prevSelectedWellsRef.current;

    const hasSelectedWellChanged =
      (currentWellIds.length !== prevWellIds.length ||
        !currentWellIds.every((id) => prevWellIds.includes(id))) &&
      prevWellIds.length > 0;

    const currentOnlyPrimaryPath = wellNetworkMapRequest?.onlyPrimaryPath;
    const hasOnlyPrimaryPathChangedSinceLastPan =
      lastPannedOnlyPrimaryPathRef.current !== undefined &&
      currentOnlyPrimaryPath !== undefined &&
      lastPannedOnlyPrimaryPathRef.current !== currentOnlyPrimaryPath;

    const availableWells = Object.keys(wellNetworkMap.wellFeatureCollections || {});
    const hasNetworkDataForSelectedWell = currentWellIds.some((wellId) =>
      availableWells.includes(wellId)
    );

    const shouldPan =
      (isWellNetworkActive ||
        hasSelectedWellChanged ||
        hasOnlyPrimaryPathChangedSinceLastPan) &&
      hasNetworkDataForSelectedWell;

    if (shouldPan && mapbox) {
      // Update processed wells AFTER we actually decide to pan
      prevSelectedWellsRef.current = currentWellIds;
      lastPannedOnlyPrimaryPathRef.current = currentOnlyPrimaryPath;

      const runPanThenRefresh = async () => {
        // Await the full animation + post-animation idle (tiles loaded) before
        // querying visible facilities — avoids icons appearing at the old viewport.
        await panToNetworkBounds();

        const logInfo = {
          selectedWell: currentWellIds[0],
          primaryPathToggle: wellNetworkMapRequest?.onlyPrimaryPath,
          date: wellNetworkMapRequest?.date ?? wellNetworkMap?.date,
          contributionPercentage: wellNetworkMapRequest?.hideConnectionsLessThanPercent,
          width: networkLineWidth
        };

        if (isWellNetworkActive) {
          sendMidstreamNavigatorButtonToggledEvent({
            source: "Well Network Toggled",
            ...logInfo
          });
        } else if (hasOnlyPrimaryPathChangedSinceLastPan) {
          sendWellNetworkShowingEvent({
            source: "Primary Path Toggled",
            ...logInfo
          });
        } else {
          sendWellNetworkShowingEvent({
            source: "Selected Well Changed",
            ...logInfo
          });
        }

        // Trigger re-query by incrementing panCompleted
        setPanCompleted((prev) => prev + 1);
      };

      runPanThenRefresh();

      if (isWellNetworkActive) {
        setWellNetworkFilterActivated(false);
      }
    }
  }, [
    showNetworkVisualOptions,
    isWellNetworkActive,
    selectedWells,
    wellNetworkMap,
    mapbox,
    panToNetworkBounds,
    setWellNetworkFilterActivated,
    wellNetworkMapRequest,
    networkLineWidth,
    sendWellNetworkShowingEvent,
    sendMidstreamNavigatorButtonToggledEvent
  ]);

  // ── Return ─────────────────────────────────────────────────────────

  return {
    // State
    isActive: showNetworkVisualOptions,
    networkLineWidth,
    wellNetworkMap: wellNetworkMap ?? undefined,
    networkDepthLegend,
    wellNetworkMapRequest,
    containerRef,
    containerWidth,
    containerHeight,

    // Actions
    clearNetworkMap,
    updateWellNetworkMapRequest,
    setNetworkLineWidth
  };
}
